import { range, rangeDuration, subtractRanges, TIME_EPSILON, type PlayRange, type TimeRange } from '../time/ranges';

/**
 * Parts of the edit played faster: a long walk, a recipe being cooked, a
 * quiet stretch of a ride. Like cuts, they are in row (source) time, so they
 * stay with what they speed up when more is cut or clips move.
 */
export interface SpeedRange extends TimeRange {
  /** How many times as fast (1.5 = 150 %). */
  readonly rate: number;
}

/** The speeds offered. */
export const SPEEDS = [1.25, 1.5, 2, 4, 8] as const;

/**
 * Up to this speed the sound plays along, sped up without turning into
 * chipmunks (pitch kept); faster parts are silent, as in a time-lapse.
 */
export const AUDIBLE_RATE_LIMIT = 2;

/** Parts shorter than this aren't worth speeding up (a sliver left over at an edge). */
export const MIN_SPEED_SEC = 0.1;

const sameRate = (a: number, b: number) => Math.abs(a - b) < 1e-6;

/**
 * Sets the speed of `span`: replaces whatever speed its parts had, and
 * merges with touching parts of the same speed. Rate 1 puts it back to normal.
 */
export function setSpeed(speeds: readonly SpeedRange[], span: TimeRange, rate: number): SpeedRange[] {
  const others = speeds.flatMap((speed) => subtractRanges([speed], [span]).map((part) => ({ ...part, rate: speed.rate })));
  const all = rate > 1 + 1e-6 ? [...others, { start: span.start, end: span.end, rate }] : others;
  const sorted = all.filter((speed) => rangeDuration(speed) >= MIN_SPEED_SEC).sort((a, b) => a.start - b.start);
  const merged: SpeedRange[] = [];
  for (const speed of sorted) {
    const last = merged.at(-1);
    if (last && sameRate(last.rate, speed.rate) && speed.start <= last.end + TIME_EPSILON) merged[merged.length - 1] = { ...last, end: Math.max(last.end, speed.end) };
    else merged.push(speed);
  }
  return merged;
}

/** Speed at source time `t` (1 outside sped-up parts). */
export function speedAt(speeds: readonly SpeedRange[], t: number): number {
  return speeds.find((speed) => t >= speed.start && t < speed.end)?.rate ?? 1;
}

/** The one speed all of `span` plays at, or null when parts of it differ. */
export function speedOf(speeds: readonly SpeedRange[], span: TimeRange): number | null {
  const inside = speeds.filter((speed) => speed.end > span.start + TIME_EPSILON && speed.start < span.end - TIME_EPSILON);
  if (inside.length === 0) return 1;
  const [first] = inside;
  const covered = rangeDuration(span) - inside.reduce((sum, speed) => sum + (Math.min(speed.end, span.end) - Math.max(speed.start, span.start)), 0);
  return inside.every((speed) => sameRate(speed.rate, first!.rate)) && covered < TIME_EPSILON * 10 ? first!.rate : null;
}

/** Kept parts of the edit with their speeds: split where a speed starts or ends. */
export function applySpeeds(keep: readonly TimeRange[], speeds: readonly SpeedRange[]): PlayRange[] {
  if (speeds.length === 0) return [...keep];
  const result: PlayRange[] = [];
  for (const kept of keep) {
    let cursor = kept.start;
    for (const speed of speeds) {
      if (speed.end <= cursor || speed.start >= kept.end) continue;
      if (speed.start > cursor) result.push(range(cursor, speed.start));
      const end = Math.min(kept.end, speed.end);
      result.push({ ...range(Math.max(cursor, speed.start), end), rate: speed.rate });
      cursor = end;
    }
    if (kept.end - cursor > TIME_EPSILON) result.push(range(cursor, kept.end));
  }
  return result.filter((part) => rangeDuration(part) > TIME_EPSILON);
}

/** Speeds after their ranges were moved by `remap` (which keeps each one's parts with their clip). */
export function remapSpeeds(speeds: readonly SpeedRange[], remap: (ranges: readonly TimeRange[]) => TimeRange[]): SpeedRange[] {
  return speeds
    .flatMap((speed) => remap([speed]).map((part) => ({ ...part, rate: speed.rate })))
    .filter((speed) => rangeDuration(speed) >= MIN_SPEED_SEC)
    .sort((a, b) => a.start - b.start);
}
